import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { QueryClient } from "@tanstack/react-query";
import { millisecondsInSecond } from "date-fns";
import { getValidToken } from "@utils";
import { FetchPayload } from "../interfaces";
import { apiClient, apiGatewayClient } from "../api/clients";


type ErrorHandler<E> = (error: AxiosError<E>) => never;

const createApiFunction = (client: typeof apiClient, apiName: string) => {
  return async <T, E = unknown>(
    { ...options }: AxiosRequestConfig,
    onErrorCustomHandler?: ErrorHandler<E>
  ): Promise<T> => {
    const config: AxiosRequestConfig = {
      ...options,
      headers: {},
    };
    try {
      const idToken = await getValidToken();
      if (idToken) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${idToken}`,
        };
      }
    } catch (err) {
      console.log("No valid auth token available");
    }

    const onSuccess = (response: AxiosResponse<FetchPayload<T>>) => {
      return response.data.payload;
    };

    const onErrorDefaultHandler: ErrorHandler<E> = (error: AxiosError<E>) => {
      console.error({
        msg: `${apiName} - onError`,
        error,
      });

      throw error;
    };
    return client(config)
      .then(onSuccess)
      .catch(onErrorCustomHandler ?? onErrorDefaultHandler);
  };
};

export const api = createApiFunction(apiClient, "Api");
export const apiGateway = createApiFunction(apiGatewayClient, "ApiGateway");

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: millisecondsInSecond * 20,
    },
  },
});
